import { CdkVirtualScrollViewport, VirtualScrollStrategy } from '@angular/cdk/scrolling';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { PhxSelectOption } from '../select.types';
import { PhxHeightPredictorFn } from '../select.types';


// TODO: Will make this configurable from the directive that will use this strategy in future if needed.
const PaddingAbove = 20;
const PaddingBelow = 30;

/**
 * Custom virtual scroll strategy for the select component.
 * This strategy is used to calculate the height of the options
 * and the offset of the scrollable container.
 */

export class PhxCustomVirtualScrollStrategy implements VirtualScrollStrategy {
  _scrolledIndexChange$ = new Subject<number>();
  scrolledIndexChange: Observable<number> = this._scrolledIndexChange$.pipe(distinctUntilChanged());

  private _viewport!: CdkVirtualScrollViewport | null;
  private _wrapper!: ChildNode | null;
  private _options: PhxSelectOption[] = [];
  private _heightPredictor!: PhxHeightPredictorFn;
  private _resizeObserver: ResizeObserver | null = null;
  private _lastViewportHeight = 0;

  attach(viewport: CdkVirtualScrollViewport): void {
    this._viewport = viewport;
    this._wrapper = viewport.getElementRef().nativeElement.childNodes[0];

    if (this._options) {
      this._viewport.setTotalContentSize(this._getTotalHeight());
      this._updateRenderedRange();
    }

    // When the CDK overlay detaches and re-attaches the panel, the viewport DOM element
    // is removed and re-inserted. The browser silently resets scrollTop to 0 without
    // firing a scroll event, leaving the rendered range stale (showing blank).
    // ResizeObserver fires when the element goes from height 0 (detached) to > 0 (attached);
    // checkViewportSize() re-measures the viewport (its cached size is stale after being
    // hidden) and then re-evaluates the rendered range.
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;

    if (typeof ResizeObserver !== 'undefined') {
      this._lastViewportHeight = viewport.getElementRef().nativeElement.clientHeight;
      this._resizeObserver = new ResizeObserver((entries) => {
        const height = entries[0]?.contentRect.height ?? 0;
        if (this._lastViewportHeight === 0 && height > 0 && this._viewport) {
          this._viewport.checkViewportSize();
        }
        this._lastViewportHeight = height;
      });
      this._resizeObserver.observe(viewport.getElementRef().nativeElement);
    }
  }

  detach(): void {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._viewport = null;
    this._wrapper = null;
  }

  onContentScrolled(): void {
    if (this._viewport) {
      this._updateRenderedRange();
    }
  }

  onDataLengthChanged(): void {
    if (!this._viewport) {
      return;
    }

    this._viewport.setTotalContentSize(this._getTotalHeight());
    this._updateRenderedRange();
  }

  onContentRendered(): void {
    /** no-op */
  }

  onRenderedOffsetChanged(): void {
    /** no-op */
  }

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    if (!this._viewport) {
      return;
    }

    const offset = this._getOffsetByOptionIdx(index);
    this._viewport.scrollToOffset(offset, behavior);
  }

  /**
   * Update the options array.
   *
   * @param options
   */
  updateOptions(options: PhxSelectOption[]) {
    this._options = options;

    if (this._viewport && this._heightPredictor) {
      this._viewport.checkViewportSize();
    }
  }

  /**
   * Update the height predictor function.
   *
   * @param predictor The function to calculate option heights
   */
  updateHeightPredictor(predictor: PhxHeightPredictorFn) {
    this._heightPredictor = predictor;

    if (this._viewport && this._options.length) {
      this._viewport.setTotalContentSize(this._getTotalHeight());
      this._updateRenderedRange();
    }
  }

  /**
   * Returns the total height of the scrollable container
   * given the size of the elements.
   */
  private _getTotalHeight(): number {
    return this._measureOptionsHeight(this._options);
  }

  /**
   * Returns the offset relative to the top of the container
   * by a provided option index.
   *
   * @param idx
   * @returns
   */
  private _getOffsetByOptionIdx(idx: number): number {
    return this._measureOptionsHeight(this._options.slice(0, idx));
  }

  /**
   * Returns the option index by a provided offset.
   *
   * @param offset
   * @returns
   */
  private _getOptionIdxByOffset(offset: number): number {
    let accumOffset = 0;

    for (let i = 0; i < this._options.length; i++) {
      const opt = this._options[i];
      const optHeight = this._getOptionHeight(opt, i);
      accumOffset += optHeight;

      if (accumOffset >= offset) {
        return i;
      }
    }

    // scrollOffset exceeded totalHeight — options were replaced with a shorter list
    // while the browser still held the old scroll position. Clamp to last valid index.
    return Math.max(0, this._options.length - 1);
  }

  /**
   * Measure options height.
   *
   * @param options
   * @returns
   */
  private _measureOptionsHeight(options: PhxSelectOption[]): number {
    return options.reduce((total: number, opt: PhxSelectOption, index: number) => total + this._getOptionHeight(opt, index), 0);
  }

  /**
   * Determine the number of renderable options
   * withing the viewport by given options index.
   *
   * @param startIdx
   * @returns
   */
  private _determineOptionsCountInViewport(startIdx: number): number {
    if (!this._viewport) {
      return 0;
    }

    let totalSize = 0;
    const viewportSize = this._viewport.getViewportSize();

    for (let i = startIdx; i < this._options.length; i++) {
      const opt = this._options[i];
      totalSize += this._getOptionHeight(opt, i);

      if (totalSize >= viewportSize) {
        return i - startIdx + 1;
      }
    }

    // Reached end of list without filling the viewport (near bottom of list).
    return this._options.length - startIdx;
  }

  /**
   * Update the range of rendered options.
   *
   * @returns
   */
  private _updateRenderedRange() {
    if (!this._viewport) {
      return;
    }

    const scrollOffset = this._viewport.measureScrollOffset();
    const scrollIdx = this._getOptionIdxByOffset(scrollOffset);
    const dataLength = this._viewport.getDataLength();
    const renderedRange = this._viewport.getRenderedRange();
    const range = {
      start: renderedRange.start,
      end: renderedRange.end,
    };

    range.start = Math.max(0, scrollIdx - PaddingAbove);
    range.end = Math.min(dataLength, scrollIdx + this._determineOptionsCountInViewport(scrollIdx) + PaddingBelow);

    this._viewport.setRenderedRange(range);
    this._viewport.setRenderedContentOffset(this._getOffsetByOptionIdx(range.start));
    this._scrolledIndexChange$.next(scrollIdx);
  }

  /**
   * Get the height of a given option.
   *
   * @param m
   * @returns
   */
  private _getOptionHeight(m: PhxSelectOption, index: number): number {
    return this._heightPredictor(m, index);
  }
}
